import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import { AttachMoney, Check, Error } from "@mui/icons-material";
import apiService from "./api"; // Import the API service

const steps = ["Member Selection", "Confirmation", "Receipt"];

const CollectCash = ({ open, onClose, onTransactionComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [collectingOfficer, setCollectingOfficer] = useState("");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [amountInWords, setAmountInWords] = useState("");

  // Fetch members when component mounts
  useEffect(() => {
    if (open) {
      fetchMembers();
    }
  }, [open]);

  // Convert amount to words
  useEffect(() => {
    if (amount) {
      const numberToWords = (num) => {
        const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
                    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
                    'seventeen', 'eighteen', 'nineteen'];
        const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty',
                    'ninety'];

        if (num < 0) return "minus " + numberToWords(Math.abs(num));
        if (num === 0) return "zero";

        if (num < 20) {
            return ones[num];
        }

        if (num < 100) {
            return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? " " + ones[num % 10] : "");
        }

        if (num < 1000) {
            return ones[Math.floor(num / 100)] + " hundred" +
                   (num % 100 !== 0 ? " and " + numberToWords(num % 100) : "");
        }

        if (num < 1000000) {
            return numberToWords(Math.floor(num / 1000)) + " thousand" +
                   (num % 1000 !== 0 ? " " + numberToWords(num % 1000) : "");
        }

        return numberToWords(Math.floor(num / 1000000)) + " million" +
               (num % 1000000 !== 0 ? " " + numberToWords(num % 1000000) : "");
      };

      // Split amount into whole number and decimal parts
      const parts = parseFloat(amount).toFixed(2).split('.');
      const wholePart = parseInt(parts[0]);
      const decimalPart = parseInt(parts[1]);

      let result = numberToWords(wholePart) + " shillings";
      if (decimalPart > 0) {
        result += " and " + numberToWords(decimalPart) + " cents";
      }

      setAmountInWords(result.charAt(0).toUpperCase() + result.slice(1));
    } else {
      setAmountInWords("");
    }
  }, [amount]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await apiService.fetchMembers();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching members:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate first step
      if (!selectedMember || !collectingOfficer || !amount || parseFloat(amount) <= 0) {
        setError("Please fill all required fields with valid values");
        return;
      }
      setError(null);
    }

    if (activeStep === 1) {
      // Process the cash collection
      processCashCollection();
      return;
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedMember(null);
    setCollectingOfficer("");
    setAmount("");
    setRemarks("");
    setError(null);
    setTransaction(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const processCashCollection = async () => {
    try {
      setLoading(true);

      const cashData = {
        memberId: selectedMember.id,
        officerName: collectingOfficer,
        amount: amount,
        remarks: remarks || "Cash Collection",
      };

      const data = await apiService.collectCash(cashData);
      setTransaction(data);
      setActiveStep((prevStep) => prevStep + 1);

      // Notify parent component that a new transaction was created
      if (onTransactionComplete) {
        onTransactionComplete();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={Array.isArray(members) ? members : []}
              getOptionLabel={(option) => `${option.name} (${option.member_id})`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Member"
                  required
                  fullWidth
                  margin="normal"
                  error={!selectedMember && error}
                />
              )}
              onChange={(event, newValue) => {
                setSelectedMember(newValue);
              }}
              value={selectedMember}
              loading={loading}
              loadingText="Loading members..."
            />

            <TextField
              label="Amount (KSh)"
              type="number"
              required
              fullWidth
              margin="normal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={!amount && error}
              inputProps={{ min: "0", step: "0.01" }}
            />

            <TextField
              label="Collecting Officer"
              required
              fullWidth
              margin="normal"
              value={collectingOfficer}
              onChange={(e) => setCollectingOfficer(e.target.value)}
              error={!collectingOfficer && error}
            />

            <TextField
              label="Remarks (Optional)"
              fullWidth
              margin="normal"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Please confirm the details below:
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body1" fontWeight="bold">Member Name:</Typography>
                  <Typography variant="body1">{selectedMember?.name || ""}</Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body1" fontWeight="bold">Member ID:</Typography>
                  <Typography variant="body1">{selectedMember?.member_id || ""}</Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body1" fontWeight="bold">Group:</Typography>
                  <Typography variant="body1">{selectedMember?.group?.name || "N/A"}</Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body1" fontWeight="bold">Phone Number:</Typography>
                  <Typography variant="body1">{selectedMember?.phone || "N/A"}</Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body1" fontWeight="bold">Amount:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    KSh {parseFloat(amount).toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body1" fontWeight="bold">Amount in Words:</Typography>
                  <Typography variant="body1" sx={{ maxWidth: "60%" }}>
                    {amountInWords}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body1" fontWeight="bold">Collecting Officer:</Typography>
                  <Typography variant="body1">{collectingOfficer}</Typography>
                </Box>

                {remarks && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body1" fontWeight="bold">Remarks:</Typography>
                    <Typography variant="body1" sx={{ maxWidth: "60%" }}>{remarks}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            <Typography variant="body2" color="text.secondary" align="center">
              Please verify all details are correct before confirming the transaction.
            </Typography>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            {transaction ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                  <Check sx={{ fontSize: 60, color: "success.main" }} />
                </Box>

                <Typography variant="h5" gutterBottom color="success.main">
                  Cash Collection Successful!
                </Typography>

                <Paper elevation={3} sx={{ p: 3, mb: 3, mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Transaction Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" fontWeight="bold">Company Ref:</Typography>
                      <Typography variant="body2">{transaction.company_ref}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" fontWeight="bold">Time:</Typography>
                      <Typography variant="body2">{new Date(transaction.created_at).toLocaleString()}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" fontWeight="bold">Member:</Typography>
                      <Typography variant="body2">{transaction.customer_name}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" fontWeight="bold">Amount:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary">KSh {parseFloat(transaction.amount).toFixed(2)}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" fontWeight="bold">Status:</Typography>
                      <Typography variant="body2" color="success.main">COMPLETED</Typography>
                    </Box>
                  </Box>
                </Paper>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  A new cash transaction has been recorded and will appear in the transactions list.
                </Typography>

                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleClose}
                    sx={{ mr: 2 }}
                  >
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {/* Add functionality to print receipt */}}
                  >
                    Print Receipt
                  </Button>
                </Box>
              </Box>
            ) : error ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                  <Error sx={{ fontSize: 60, color: "error.main" }} />
                </Box>

                <Typography variant="h5" gutterBottom color="error.main">
                  Transaction Failed
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  {error}
                </Typography>

                <Button variant="contained" color="primary" onClick={handleBack}>
                  Try Again
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h6">Processing Transaction...</Typography>
              </Box>
            )}
          </Box>
        );

      default:
        return "Unknown step";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <AttachMoney sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6">Cash Collection</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && activeStep !== 2 && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      {activeStep !== 2 && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            {activeStep === steps.length - 2 ? "Submit" : "Next"}
            {loading && <CircularProgress size={24} sx={{ ml: 1 }} />}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CollectCash;